import React, { createContext, useContext, useMemo, useState, useCallback } from "react";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState({}); // {id: {item, qty}}
  const [open, setOpen] = useState(false);

  const add = useCallback((item, qty = 1) => {
    setItems((prev) => {
      const cur = prev[item.id]?.qty || 0;
      return { ...prev, [item.id]: { item, qty: cur + qty } };
    });
  }, []);

  const update = useCallback((id, qty) => {
    setItems((prev) => {
      if (qty <= 0) {
        const { [id]: _omit, ...rest } = prev;
        return rest;
      }
      if (!prev[id]) return prev;
      return { ...prev, [id]: { ...prev[id], qty } };
    });
  }, []);

  const remove = useCallback((id) => {
    setItems((prev) => {
      const { [id]: _omit, ...rest } = prev;
      return rest;
    });
  }, []);

  const clear = useCallback(() => setItems({}), []);

  const { count, subtotal, list } = useMemo(() => {
    const list = Object.values(items);
    const count = list.reduce((s, x) => s + x.qty, 0);
    const subtotal = list.reduce((s, x) => s + x.qty * x.item.price, 0);
    return { count, subtotal: Math.round(subtotal * 100) / 100, list };
  }, [items]);

  const value = { items, list, count, subtotal, add, update, remove, clear, open, setOpen };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
